import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";

import {
  adaptiveSamplingKeys,
  fetchSurface,
  type SurfaceParams,
} from "../adaptive-sampling/api";
import type {
  CampaignState,
  SurfaceResponse,
} from "../adaptive-sampling/types";
import type { TailorEvidenceMode } from "./adbTailorSlice";
import { DEFAULT_TAILOR_TRUST_LIMITS } from "./adbTailorSlice";
import { convexHull2d, type SliceResult } from "./gpField";
import {
  buildTransportStalks,
  projectRuns,
  type ProjectedRun,
  type TransportStalk,
} from "./TailorGpSurface";
import {
  CellTile,
  CorrelogramThreeLayer,
  rankAxesByInfluence,
  varyingPairs,
  type CellPair,
} from "./TailorGpCorrelogram";
import { Icons } from "../thread-v4/icons";

const CAMPAIGN_CELL_RESOLUTION = 16;
const MAX_PAIR_CONCURRENCY = 2;
const HELD_DEBOUNCE_MS = 120;
const DEFAULT_VIEW = { pitch: 0.62, yaw: -0.52, zoom: 1 };
const ZOOM_LIMITS: [number, number] = [0.35, 6];

export interface PairSurfaceRequest {
  pair: CellPair;
  params: SurfaceParams;
}

export interface LoadPairSurfacesOptions {
  requests: PairSurfaceRequest[];
  signal: AbortSignal;
  fetchPair: (request: PairSurfaceRequest, signal: AbortSignal) => Promise<SurfaceResponse>;
  maxConcurrency?: number;
  onSurface?: (request: PairSurfaceRequest, response: SurfaceResponse, completed: number, total: number) => void;
}

function abortError(): DOMException {
  return new DOMException("Tailor pair-surface generation was superseded", "AbortError");
}

export interface FetchPairSurfaceCachedOptions {
  queryClient: QueryClient;
  campaignId: string;
  request: PairSurfaceRequest;
  signal: AbortSignal;
  fetcher?: typeof fetchSurface;
}

/**
 * Fetch one current-generation pair without sharing an aborted StrictMode
 * promise. Completed surfaces still enter TanStack's cache for instant view
 * switches and held-slice revisits.
 */
export async function fetchPairSurfaceCached({
  queryClient,
  campaignId,
  request,
  signal,
  fetcher = fetchSurface,
}: FetchPairSurfaceCachedOptions): Promise<SurfaceResponse> {
  const queryKey = adaptiveSamplingKeys.surface(campaignId, request.params);
  const cached = queryClient.getQueryData<SurfaceResponse>(queryKey);
  if (cached) return cached;

  let lastError: unknown;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (signal.aborted) throw abortError();
    try {
      const response = await fetcher(campaignId, request.params, signal);
      queryClient.setQueryData(queryKey, response);
      return response;
    } catch (error) {
      if (signal.aborted) throw abortError();
      lastError = error;
    }
  }
  throw lastError;
}

/**
 * Load each unordered pair exactly once through a small worker pool. The caller
 * owns the AbortController so a new held slice/checkpoint can cancel stale work.
 */
export async function loadPairSurfaces({
  requests,
  signal,
  fetchPair,
  maxConcurrency = MAX_PAIR_CONCURRENCY,
  onSurface,
}: LoadPairSurfacesOptions): Promise<Map<string, SurfaceResponse>> {
  const unique = new Map<string, PairSurfaceRequest>();
  for (const request of requests) {
    if (request.pair.axisX === request.pair.axisY) continue;
    const unorderedKey = [request.pair.axisX, request.pair.axisY].sort().join("|");
    if (!unique.has(unorderedKey)) unique.set(unorderedKey, request);
  }
  const queue = [...unique.values()];
  const responses = new Map<string, SurfaceResponse>();
  const workers = Math.max(1, Math.min(Math.trunc(maxConcurrency) || 1, queue.length || 1));
  let cursor = 0;
  let completed = 0;

  const worker = async () => {
    for (;;) {
      if (signal.aborted) throw abortError();
      const index = cursor;
      cursor += 1;
      const request = queue[index];
      if (!request) return;
      const response = await fetchPair(request, signal);
      if (signal.aborted) throw abortError();
      responses.set(request.pair.key, response);
      completed += 1;
      onSurface?.(request, response, completed, queue.length);
    }
  };

  await Promise.all(Array.from({ length: workers }, () => worker()));
  return responses;
}

function flattenGrid(grid: number[][], nx: number, ny: number): (number | null)[] {
  const values: (number | null)[] = new Array(nx * ny);
  for (let y = 0; y < ny; y += 1) {
    for (let x = 0; x < nx; x += 1) {
      const value = grid[y]?.[x];
      values[y * nx + x] = typeof value === "number" && Number.isFinite(value) ? value : null;
    }
  }
  return values;
}

/** Convert the campaign endpoint's y-major matrices into the shared renderer. */
export function surfaceResponseToSlice(pair: CellPair, response: SurfaceResponse): SliceResult {
  const nx = response.x.length;
  const ny = response.y.length;
  const z = flattenGrid(response.mean, nx, ny);
  const sigma = flattenGrid(response.sigma, nx, ny);
  let zMin = Number.POSITIVE_INFINITY;
  let zMax = Number.NEGATIVE_INFINITY;
  for (const value of z) {
    if (value == null) continue;
    zMin = Math.min(zMin, value);
    zMax = Math.max(zMax, value);
  }
  // Fold transported means and their Monte-Carlo members into the cell's
  // value range: corrected evidence is real observed data, not bounded by
  // the smoothed posterior grid, and a member outside [zMin, zMax] would
  // pierce the cell frame and misread against the axis ticks. Folding at
  // slice construction keeps the morph (lerpSliceResult) consistent and the
  // range stable across raw/corrected switches.
  for (const evidence of Object.values(response.transport ?? {})) {
    if (Number.isFinite(evidence.mean)) {
      zMin = Math.min(zMin, evidence.mean);
      zMax = Math.max(zMax, evidence.mean);
    }
    for (const member of evidence.mc ?? []) {
      if (Number.isFinite(member)) {
        zMin = Math.min(zMin, member);
        zMax = Math.max(zMax, member);
      }
    }
  }
  if (!Number.isFinite(zMin) || !Number.isFinite(zMax)) {
    zMin = 0;
    zMax = 1;
  } else if (Math.abs(zMax - zMin) < 1e-12) {
    zMin -= 0.5;
    zMax += 0.5;
  }
  return {
    xAxis: pair.axisX,
    yAxis: pair.axisY,
    xVals: response.x,
    yVals: response.y,
    z,
    sigma,
    nx,
    ny,
    zMin,
    zMax,
  };
}

const MORPH_DURATION_MS = 300;

/** Element-wise interpolation between two slices of the same cell.

 * Heights, uncertainty, and the colour range all derive from `z`/`sigma`/
 * `zMin`/`zMax`, so lerping those four morphs the rendered surface end to
 * end. Grids whose shape or axes differ (a different cell, a resolution
 * change) snap to the target — index-wise interpolation would be meaningless.
 * A masked (`null`) endpoint also snaps: there is no number to tween through.
 */
export function lerpSliceResult(from: SliceResult, to: SliceResult, t: number): SliceResult {
  if (t >= 1) return to;
  if (
    from.nx !== to.nx
    || from.ny !== to.ny
    || from.xAxis !== to.xAxis
    || from.yAxis !== to.yAxis
  ) {
    return to;
  }
  const lerp = (a: number | null, b: number | null): number | null =>
    a == null || b == null ? b : a + (b - a) * t;
  return {
    ...to,
    z: to.z.map((value, index) => lerp(from.z[index] ?? null, value)),
    sigma: to.sigma.map((value, index) => lerp(from.sigma[index] ?? null, value)),
    zMin: from.zMin + (to.zMin - from.zMin) * t,
    zMax: from.zMax + (to.zMax - from.zMax) * t,
  };
}

function slicesEqual(left: SliceResult, right: SliceResult): boolean {
  const arraysEqual = <T,>(a: readonly T[], b: readonly T[]) =>
    a.length === b.length && a.every((value, index) => value === b[index]);
  return left === right || (
    left.xAxis === right.xAxis
    && left.yAxis === right.yAxis
    && left.nx === right.nx
    && left.ny === right.ny
    && left.zMin === right.zMin
    && left.zMax === right.zMax
    && arraysEqual(left.xVals, right.xVals)
    && arraysEqual(left.yVals, right.yVals)
    && arraysEqual(left.z, right.z)
    && arraysEqual(left.sigma, right.sigma)
  );
}

/** Morph the correlogram cells toward their latest fitted surfaces.

 * Each target change (a refit landing after a held-slider move) starts a
 * short rAF tween from whatever is currently displayed, so figures reshape
 * smoothly instead of popping. Cells with no previous surface appear
 * directly — there is nothing to morph from. Respects reduced-motion.
 */
export function useMorphedSlices(
  target: Map<string, SliceResult>,
  durationMs: number = MORPH_DURATION_MS,
): Map<string, SliceResult> {
  const [display, setDisplay] = useState(target);
  const displayRef = useRef(target);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (frameRef.current != null) {
      window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    const from = displayRef.current;
    const settle = () => {
      displayRef.current = target;
      setDisplay(target);
    };
    const unchanged = from.size === target.size
      && [...target].every(([key, slice]) => {
        const previous = from.get(key);
        return previous != null && slicesEqual(previous, slice);
      });
    if (unchanged) return;
    const reduceMotion = typeof window.matchMedia === "function"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const animatable = [...target].some(([key, slice]) => {
      const previous = from.get(key);
      return previous != null && !slicesEqual(previous, slice);
    });
    if (reduceMotion || !animatable) {
      settle();
      return;
    }
    const start = performance.now();
    const step = (now: number) => {
      const linear = Math.min(1, (now - start) / durationMs);
      const eased = linear * linear * (3 - 2 * linear);
      if (linear >= 1) {
        frameRef.current = null;
        settle();
        return;
      }
      const frame = new Map<string, SliceResult>();
      for (const [key, slice] of target) {
        const previous = from.get(key);
        frame.set(
          key,
          previous && !slicesEqual(previous, slice)
            ? lerpSliceResult(previous, slice, eased)
            : slice,
        );
      }
      displayRef.current = frame;
      setDisplay(frame);
      frameRef.current = window.requestAnimationFrame(step);
    };
    frameRef.current = window.requestAnimationFrame(step);
    return () => {
      if (frameRef.current != null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [durationMs, target]);

  return display;
}

function useDebouncedHeldValues(values: Record<string, number>): Record<string, number> {
  const signature = JSON.stringify(Object.entries(values).sort(([left], [right]) => left.localeCompare(right)));
  const [debounced, setDebounced] = useState(values);
  useEffect(() => {
    const entries = JSON.parse(signature) as Array<[string, number]>;
    const timer = window.setTimeout(() => setDebounced(Object.fromEntries(entries)), HELD_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [signature]);
  return debounced;
}

function rowId(row: Record<string, unknown>): string | null {
  const value = row._row_id ?? row.id ?? row.runId;
  return typeof value === "string" || typeof value === "number" ? String(value) : null;
}

function pairEvidence(
  rows: Record<string, unknown>[],
  pair: CellPair,
  output: string,
  response: SurfaceResponse | undefined,
  mode: TailorEvidenceMode,
): { runs: ProjectedRun[]; stalks: TransportStalk[]; hull: Array<{ x: number; y: number }> } {
  const raw = projectRuns(rows, pair.axisX, pair.axisY, output);
  const transport = response?.transport ?? {};
  const compatible = raw.filter((run) => {
    const id = run.row ? rowId(run.row) : null;
    const evidence = id ? transport[id] : undefined;
    return evidence != null
      && evidence.hiddenDistance <= DEFAULT_TAILOR_TRUST_LIMITS.compatibleRmsLimit
      && evidence.hiddenMaxDelta <= DEFAULT_TAILOR_TRUST_LIMITS.compatibleMaxDeltaLimit;
  });
  const alignedRaw: ProjectedRun[] = [];
  const corrected: ProjectedRun[] = [];
  for (const run of raw) {
    const id = run.row ? rowId(run.row) : null;
    const evidence = id ? transport[id] : undefined;
    if (!evidence) continue;
    alignedRaw.push(run);
    corrected.push({ ...run, value: evidence.mean, mc: evidence.mc });
  }
  return {
    runs: mode === "raw" ? raw : mode === "compatible" ? compatible : corrected,
    stalks: mode === "corrected" ? buildTransportStalks(alignedRaw, corrected) : [],
    hull: convexHull2d(compatible.map((run) => ({ x: run.x, y: run.y }))),
  };
}

export interface TailorCampaignGpCorrelogramProps {
  campaignId?: string;
  state?: CampaignState;
  output: string;
  rows: Record<string, unknown>[];
  heldValues: Record<string, number>;
  pointLimit: number;
  evidenceMode: TailorEvidenceMode;
  axisLabels?: Record<string, string>;
  /** Right-click "Why this point?" on a run sphere — receives SamplePoint.id. */
  onPointContextMenu?: (pointId: string, clientX: number, clientY: number) => void;
}

/**
 * Campaign-specific High-D view. Every cell comes from the exact Mannequin
 * Tailor ARD residual-GP posterior endpoint; sample rows never define a mesh.
 */
export function TailorCampaignGpCorrelogram({
  campaignId,
  state,
  output,
  rows,
  heldValues,
  pointLimit,
  evidenceMode,
  axisLabels,
  onPointContextMenu,
}: TailorCampaignGpCorrelogramProps) {
  const queryClient = useQueryClient();
  const held = useDebouncedHeldValues(heldValues);
  const inputSignature = JSON.stringify((state?.config.inputs ?? []).map((axis) => [
    axis.name, axis.mode, axis.min, axis.max, axis.fixedValue,
  ]));
  const inputAxes = useMemo(
    () => (state?.config.inputs ?? []).filter((axis) => axis.mode === "range" && axis.min != null && axis.max != null),
    // The campaign query can replace state on every poll; its numeric input
    // contract, not object identity, determines this matrix.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputSignature],
  );
  const axisNames = inputAxes.map((axis) => axis.name);
  const axisNamesSignature = axisNames.join("|");
  const rankedAxes = useMemo(
    () => rankAxesByInfluence(rows, axisNames, output).map((item) => item.axis),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, axisNamesSignature, output],
  );
  const pairs = useMemo(() => varyingPairs(rankedAxes), [rankedAxes]);
  const heldSignature = JSON.stringify(Object.entries(held).sort(([left], [right]) => left.localeCompare(right)));
  const requests = useMemo<PairSurfaceRequest[]>(() => pairs.map((pair) => {
    const slice = Object.fromEntries(inputAxes
      .filter((axis) => axis.name !== pair.axisX && axis.name !== pair.axisY)
      .map((axis) => {
        const requested = held[axis.name];
        const fallback = axis.mode === "fixed"
          ? axis.fixedValue ?? axis.min ?? axis.max ?? 0
          : ((axis.min ?? 0) + (axis.max ?? 1)) / 2;
        return [axis.name, typeof requested === "number" && Number.isFinite(requested) ? requested : fallback];
      }));
    return {
      pair,
      params: {
        output,
        xInput: pair.axisX,
        yInput: pair.axisY,
        slice,
        resolution: CAMPAIGN_CELL_RESOLUTION,
        pointLimit,
        // MC clouds are standard across every evidence view. Requested
        // unconditionally so raw <-> corrected switching never refetches;
        // 16 members per point — a small cell cannot resolve more, and 21
        // pair fetches share one backend worker.
        transportMonteCarlo: true,
        transportMcMembers: 16,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [pairs, inputSignature, heldSignature, output, pointLimit]);
  const requestSignature = JSON.stringify([
    campaignId,
    output,
    pointLimit,
    requests.map((request) => [request.pair.key, request.params.slice]),
  ]);
  const [responses, setResponses] = useState<Map<string, SurfaceResponse>>(() => new Map());
  const [progress, setProgress] = useState({ completed: 0, total: pairs.length });
  const [loadError, setLoadError] = useState<string | null>(null);
  const generationRef = useRef(0);

  useEffect(() => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    const controller = new AbortController();
    // Keep the previous surfaces on screen while their refits stream in — a
    // held-slider move must morph each figure into its new shape, never blank
    // the grid. Only cells whose axis pair left the correlogram are pruned.
    const requestedKeys = new Set(requests.map((request) => request.pair.key));
    setResponses((current) => {
      if ([...current.keys()].every((key) => requestedKeys.has(key))) return current;
      const next = new Map(current);
      for (const key of [...next.keys()]) {
        if (!requestedKeys.has(key)) next.delete(key);
      }
      return next;
    });
    setProgress({ completed: 0, total: requests.length });
    setLoadError(null);

    if (!campaignId || !state || pointLimit <= 0 || requests.length === 0) {
      return () => controller.abort();
    }

    void loadPairSurfaces({
      requests,
      signal: controller.signal,
      maxConcurrency: MAX_PAIR_CONCURRENCY,
      fetchPair: (request, generationSignal) => {
        // Drive cancellation from this rendered generation, not TanStack's
        // query AbortSignal. React StrictMode mounts/cleans/remounts effects;
        // sharing the first mount's aborted fetchQuery promise with the second
        // leaves a healthy current generation failed with "signal is aborted".
        // The backend already single-flights the expensive GP fit, while this
        // two-worker pool bounds the independent surface evaluations.
        return fetchPairSurfaceCached({
          queryClient,
          campaignId,
          request,
          signal: generationSignal,
        });
      },
      onSurface: (request, response, completed, total) => {
        if (controller.signal.aborted || generation !== generationRef.current) return;
        setResponses((current) => {
          const next = new Map(current);
          next.set(request.pair.key, response);
          return next;
        });
        setProgress({ completed, total });
      },
    }).catch((error: unknown) => {
      if (controller.signal.aborted || generation !== generationRef.current) return;
      setLoadError(error instanceof Error ? error.message : "Tailor posterior slices failed to load");
    });

    return () => controller.abort();
    // requestSignature is a stable, content-derived generation key. Including
    // state/requests object identity would restart all 21 jobs on each poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, queryClient, requestSignature]);

  const targetSlices = useMemo(() => {
    const next = new Map<string, SliceResult>();
    for (const pair of pairs) {
      const response = responses.get(pair.key);
      if (response) next.set(pair.key, surfaceResponseToSlice(pair, response));
    }
    return next;
  }, [pairs, responses]);
  // Held-slider refits morph the previous figure into the new one instead of
  // popping: the renderer rebuilds from these interpolated frames.
  const slices = useMorphedSlices(targetSlices);
  const { runsByKey, stalksByKey, hullsByKey } = useMemo(() => {
    const runs = new Map<string, ProjectedRun[]>();
    const stalks = new Map<string, TransportStalk[]>();
    const hulls = new Map<string, Array<{ x: number; y: number }>>();
    for (const pair of pairs) {
      const evidence = pairEvidence(rows, pair, output, responses.get(pair.key), evidenceMode);
      runs.set(pair.key, evidence.runs);
      stalks.set(pair.key, evidence.stalks);
      hulls.set(pair.key, evidence.hull);
    }
    return { runsByKey: runs, stalksByKey: stalks, hullsByKey: hulls };
  }, [evidenceMode, output, pairs, responses, rows]);
  const colorsByKey = useMemo(() => new Map<string, Array<[number, number, number]>>(), []);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const cellRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [view, setView] = useState(DEFAULT_VIEW);
  const dragRef = useRef<{ x: number; y: number; view: typeof DEFAULT_VIEW } | null>(null);

  // Plain wheel / pinch zooms every cell together — the same gesture the 3-D
  // surface view uses (no modifier gate; a ctrl/cmd gate left the wheel dead
  // for anyone who didn't know the shortcut). Shift+wheel pans the tall grid
  // vertically, since zoom now owns the plain wheel and the scrollbar is the
  // only other way to reach lower rows. Native non-passive listener —
  // React's onWheel is passive and cannot preventDefault the grid scroll or
  // the browser's ctrl+wheel page zoom.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      if (event.shiftKey) {
        const host = root.closest<HTMLElement>(".v5-adbc-gpc-host");
        // Trackpads report sideways shift-scroll in deltaX; wheels in deltaY.
        if (host) host.scrollTop += event.deltaY || event.deltaX;
        return;
      }
      setView((current) => ({
        ...current,
        zoom: Math.min(
          ZOOM_LIMITS[1],
          Math.max(ZOOM_LIMITS[0], (current.zoom ?? 1) * Math.exp(-event.deltaY * 0.0022)),
        ),
      }));
    };
    root.addEventListener("wheel", onWheel, { passive: false });
    return () => root.removeEventListener("wheel", onWheel);
  }, [campaignId, state, pointLimit]);
  const label = (axis: string) => axisLabels?.[axis] ?? axis;

  if (!campaignId || !state || pointLimit <= 0) {
    return (
      <div className="v5-adbc-chart-empty" data-testid="campaign-gp-correlogram-waiting">
        <Icons.Refresh size={16} />Waiting for the first fitted Tailor checkpoint…
      </div>
    );
  }

  return (
    <div className="v5-adbc-gpc" data-testid="tailor-campaign-gp-correlogram">
      <div className="v5-adb-tailor-strip" style={{ marginBottom: 6, alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <span className="v5-adbc-panel-note v5-adbc-gpc-note" data-testid="campaign-gpc-truth-label">
          <Icons.Sliders size={12} /> Tailor ARD residual-GP posterior · {pointLimit} acquired points · {pairs.length} unique pairwise slices
        </span>
        <span className="v5-adb-chip" aria-live="polite" data-testid="campaign-gpc-progress">
          {progress.completed < progress.total ? `Fitting surfaces ${progress.completed}/${progress.total}` : `${progress.total}/${progress.total} ready`}
        </span>
        <button type="button" className="v5-adb-chip" onClick={() => setView(DEFAULT_VIEW)}>
          <Icons.Refresh size={11} /> Reset 3D
        </button>
      </div>
      {loadError ? (
        <div className="v5-adbc-chart-empty" data-testid="campaign-gpc-error">
          <Icons.Info size={16} />Tailor posterior update failed: {loadError}
        </div>
      ) : null}
      <div className="v5-adbc-gpc-host" style={{ position: "relative", maxHeight: "72vh", overflowY: "auto" }}>
        <div
          ref={rootRef}
          className="v5-adbc-gpc-grid"
          data-testid="campaign-gpc-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: 8,
            position: "relative",
            touchAction: "none",
            userSelect: "none",
            cursor: "grab",
          }}
          onPointerDown={(event) => {
            // Right-click belongs to the per-point "Why this point?" menu —
            // capturing it here would start a rotate-drag and swallow the
            // contextmenu event.
            if (event.button === 2) return;
            event.currentTarget.setPointerCapture(event.pointerId);
            dragRef.current = { x: event.clientX, y: event.clientY, view };
          }}
          onPointerMove={(event) => {
            const drag = dragRef.current;
            if (!drag) return;
            setView((current) => ({
              ...current,
              yaw: drag.view.yaw + (event.clientX - drag.x) * 0.01,
              pitch: Math.max(-1.2, Math.min(1.2, drag.view.pitch + (event.clientY - drag.y) * 0.008)),
            }));
          }}
          onPointerUp={(event) => {
            dragRef.current = null;
            if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
          }}
          onPointerCancel={() => { dragRef.current = null; }}
        >
          {pairs.map((pair) => (
            <CellTile
              key={pair.key}
              cellKey={pair.key}
              xLabel={label(pair.axisX)}
              yLabel={label(pair.axisY)}
              cellRefs={cellRefs}
            />
          ))}
          <CorrelogramThreeLayer
            slices={slices}
            runsByKey={runsByKey}
            stalksByKey={stalksByKey}
            hullsByKey={hullsByKey}
            colorsByKey={colorsByKey}
            cellRefs={cellRefs}
            rootRef={rootRef}
            view={view}
            axisLabels={axisLabels}
            onPointContextMenu={onPointContextMenu}
          />
        </div>
      </div>
      <div className="v5-adbc-panel-note" style={{ marginTop: 8 }}>
        Every cell is an exact backend Tailor posterior slice at the held values above · opacity = posterior certainty · spheres = acquired {output} evidence
        {evidenceMode === "corrected" ? " · transported points render as 16-member Monte-Carlo clouds (density = certainty) · stalks = posterior transport from measured to held conditions" : ""} · drag to rotate all cells · scroll or pinch to zoom · shift-scroll to pan the grid · right-click a sphere for Why this point?
      </div>
    </div>
  );
}

export default TailorCampaignGpCorrelogram;
